/**
 * HPH Collection AJAX (B1 - vet IT feedback)
 * Turns the whole left pane into instant, no-reload interactions on the COLLECTION page:
 *  - filter checkboxes, sort, price -> refine WITHIN the current collection (Section Rendering API)
 *  - active-filter pills, "Clear all", numbered pagination links -> same
 *  - category rail links (Dogs > Dog Beds ...) -> switch collection WITHOUT a full reload (full-page fetch)
 *  - "Load more" append, grid-density toggle, mobile sidebar drawer, read-more -> delegated so they survive swaps
 *
 * Progressive enhancement: only runs when #hph-filter-form has [data-hph-ajax] (collection page only).
 * The search page keeps its native GET reload (hph-collection-filters.js, which bails out here).
 * Any failure falls back to a normal full navigation (never blocks the user).
 *
 * Scroll is PRESERVED on refine (the whole point of B1: no jump to top). A category switch
 * scrolls to the top of the listing (expected for a page change). Real <a href> are kept
 * everywhere, so SEO, open-in-new-tab and the back/forward buttons all keep working (popstate
 * re-renders). Quick-add (delegated on document) and scroll-reveal are unaffected by the swaps.
 *
 * DOM swaps move server-rendered nodes from a parsed document via appendChild (which adopts
 * cross-document), never innerHTML - same pattern as the original Load more, and the injected
 * markup is our own first-party theme HTML, byte-identical to a full render (no visual change).
 *
 * Created: June 17, 2026.
 */
(function () {
  'use strict';

  var form = document.getElementById('hph-filter-form');
  if (!form || !form.hasAttribute('data-hph-ajax')) return;

  var sectionId = form.getAttribute('data-section-id') || '';
  var section = form.closest('.hph-collection') || document;
  var liveRegion = section.querySelector('[data-hph-results-live]');
  var controller = null;
  /* The collection path currently DISPLAYED. Tracked (not read live from location) because on
     popstate location has already changed - we need the previous path to know refine vs navigate. */
  var currentPath = location.pathname;

  /* Links we own. Everything else (product titles/images, vendor links) navigates normally. */
  var LINK_SELECTOR = [
    '.hph-active-filters__pill',
    '.hph-active-filters__clear',
    '.hph-pagination__link[href]',
    '.hph-cnav__link',
    '.hph-cnav__sublink'
  ].join(',');

  /* ---------- small helpers ---------- */

  function applyGridDensity() {
    var cols = localStorage.getItem('hph-grid-cols');
    if (!cols) return;
    var grid = section.querySelector('[data-hph-grid]');
    if (grid) grid.setAttribute('data-cols', cols);
    var wrap = section.querySelector('[data-hph-grid-toggle]');
    if (wrap) {
      wrap.querySelectorAll('[data-grid-cols]').forEach(function (b) {
        b.classList.toggle('hph-grid-toggle__btn--active', b.getAttribute('data-grid-cols') === cols);
      });
    }
  }

  /* JS visitors use Load more; hide the numbered pager (no-JS keeps it as fallback). */
  function hideNumberedPagination() {
    var pager = section.querySelector('.hph-pagination');
    if (pager && section.querySelector('[data-hph-load-more]')) pager.style.display = 'none';
  }

  function announce() {
    if (!liveRegion) return;
    var count = section.querySelector('.hph-collection__count');
    if (count) liveRegion.textContent = count.textContent.trim();
  }

  function setLoading(on) {
    var main = section.querySelector('.hph-collection__main');
    if (!main) return;
    main.classList.toggle('is-loading', !!on);
    if (on) main.setAttribute('aria-busy', 'true');
    else main.removeAttribute('aria-busy');
  }

  function buildFormUrl() {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (value !== '' && value != null) params.append(key, value);
    });
    var qs = params.toString();
    return location.pathname + (qs ? '?' + qs : '');
  }

  function fetchDoc(url) {
    if (controller) controller.abort();
    controller = new AbortController();
    return fetch(url, {
      signal: controller.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  /* Replace a live element's children with the parsed element's children.
     appendChild adopts nodes across documents - no innerHTML, no re-parsing of strings. */
  function moveInto(current, next) {
    if (!current || !next) return false;
    while (current.firstChild) current.removeChild(current.firstChild);
    while (next.firstChild) current.appendChild(next.firstChild);
    return true;
  }

  function swap(selector, doc) {
    return moveInto(section.querySelector(selector), doc.querySelector(selector));
  }

  /* On a category switch, sync the per-collection SEO head tags from the fetched page so the
     canonical (and og/meta) match the new URL, not the page we started on. Surgical: only the
     tags that differ per collection, attribute-copy only (no node add/remove, no head rebuild). */
  function syncHead(doc) {
    if (!doc.head) return;
    var pairs = [
      ['link[rel="canonical"]', 'href'],
      ['meta[name="description"]', 'content'],
      ['meta[property="og:url"]', 'content'],
      ['meta[property="og:title"]', 'content'],
      ['meta[property="og:description"]', 'content'],
      ['meta[name="twitter:title"]', 'content'],
      ['meta[name="twitter:description"]', 'content']
    ];
    pairs.forEach(function (p) {
      var live = document.head.querySelector(p[0]);
      var next = doc.head.querySelector(p[0]);
      if (live && next) live.setAttribute(p[1], next.getAttribute(p[1]) || '');
    });
  }

  /* Keep the mobile drawer's "Show N results" bar in sync with the live product count. The
     count source (.hph-collection__count) lives in the swapped main column, so we re-read it
     after every refine. */
  function syncApplyCount() {
    var btn = section.querySelector('[data-hph-sidebar-apply]');
    if (!btn) return;
    var countEl = section.querySelector('.hph-collection__count');
    var n = countEl ? parseInt((countEl.textContent || '').replace(/[^0-9]/g, ''), 10) : NaN;
    if (isNaN(n)) return;
    btn.textContent = 'Show ' + n + ' result' + (n === 1 ? '' : 's');
  }

  function isDrawerOpen() {
    var sb = section.querySelector('[data-hph-collection-sidebar]');
    return !!(sb && sb.classList.contains('hph-collection__sidebar--open'));
  }

  function afterSwap() {
    applyGridDensity();
    hideNumberedPagination();
    announce();
    syncApplyCount();
  }

  /* ---------- two modes ---------- */

  function refine(doc) {
    /* The sidebar is its own scroll container (sticky + overflow-y:auto). Replacing the filter
       block resets its scrollTop to 0 = the left rail jumps to top. Capture + restore it so the
       rail stays exactly where the user is (the products column is untouched anyway). */
    /* Desktop scrolls on the <aside>; mobile scrolls on the inner .hph-collection__sidebar-scroll
       (the aside is overflow:hidden there). Capture both; the non-scrolling one is just 0. */
    var rail = section.querySelector('.hph-collection__sidebar');
    var railInner = section.querySelector('.hph-collection__sidebar-scroll');
    var railTop = rail ? rail.scrollTop : 0;
    var innerTop = railInner ? railInner.scrollTop : 0;
    if (!swap('.hph-collection__main', doc)) throw new Error('no main');
    swap('.hph-cfilters', doc); /* sidebar filter counts + checked/disabled states */
    if (rail) rail.scrollTop = railTop;
    if (railInner) railInner.scrollTop = innerTop;
    afterSwap();
  }

  function navigateTo(doc) {
    if (!doc.querySelector('.hph-collection__main')) throw new Error('no main');
    /* breadcrumb lives in its own section above the listing */
    moveInto(document.querySelector('.hph-collection-breadcrumbs'), doc.querySelector('.hph-collection-breadcrumbs'));
    swap('.hph-collection__header', doc);
    swap('.hph-cnav', doc);     /* active link + which group is open (server-computed) */
    swap('.hph-cfilters', doc); /* new collection = new filter set */
    swap('.hph-collection__main', doc);
    if (doc.title) document.title = doc.title;
    syncHead(doc); /* canonical + og/meta follow the new collection URL */
    closeDrawer();
    afterSwap();
    /* Scroll is intentionally preserved (no jump): switching category keeps the user exactly
       where they were, consistent with filtering. The sticky header keeps navigation reachable. */
  }

  function run(url, opts) {
    opts = opts || {};
    var u;
    try { u = new URL(url, location.origin); } catch (e) { window.location.href = url; return; }
    var isNavigate = u.pathname !== currentPath;
    var fetchUrl = isNavigate
      ? u.href
      : u.href + (u.search ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);

    setLoading(true);
    fetchDoc(fetchUrl)
      .then(function (doc) {
        if (isNavigate) navigateTo(doc); else refine(doc);
        if (opts.push !== false) history.pushState({ hphAjax: true }, '', u.href);
        currentPath = u.pathname;
        setLoading(false);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return; /* superseded by a newer request */
        window.location.href = u.href; /* hard fallback, never block the user */
      });
  }

  /* ---------- mobile drawer (collection only; replicated from hph-collection-filters.js so
       it survives swaps - the toggle button lives in the swapped toolbar) ---------- */

  function openDrawer() {
    var sb = section.querySelector('[data-hph-collection-sidebar]');
    if (!sb) return;
    sb.classList.add('hph-collection__sidebar--open');
    var bd = section.querySelector('[data-hph-sidebar-backdrop]');
    if (bd) bd.hidden = false;
    document.body.style.overflow = 'hidden';
    var tg = section.querySelector('[data-hph-sidebar-toggle]');
    if (tg) tg.setAttribute('aria-expanded', 'true');
  }

  function closeDrawer() {
    var sb = section.querySelector('[data-hph-collection-sidebar]');
    if (!sb) return;
    sb.classList.remove('hph-collection__sidebar--open');
    var bd = section.querySelector('[data-hph-sidebar-backdrop]');
    if (bd) bd.hidden = true;
    document.body.style.overflow = '';
    var tg = section.querySelector('[data-hph-sidebar-toggle]');
    if (tg) tg.setAttribute('aria-expanded', 'false');
  }

  /* ---------- Load more (append) ---------- */

  function loadMore(btn) {
    var url = btn.getAttribute('data-url');
    if (!url || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.textContent = 'Loading...';
    fetchDoc(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId))
      .then(function (doc) {
        var grid = section.querySelector('[data-hph-grid]');
        var newGrid = doc.querySelector('[data-hph-grid]');
        if (grid && newGrid) {
          while (newGrid.children.length) grid.appendChild(newGrid.children[0]);
        }
        var nextBtn = doc.querySelector('[data-hph-load-more]');
        if (nextBtn && nextBtn.getAttribute('data-url')) {
          btn.setAttribute('data-url', nextBtn.getAttribute('data-url'));
          btn.classList.remove('is-loading');
          btn.textContent = 'Load more';
        } else if (btn.parentNode) {
          btn.parentNode.removeChild(btn);
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        btn.classList.remove('is-loading');
        btn.textContent = 'Load more';
      });
  }

  /* ---------- delegated events (bound once on stable parents, survive swaps) ---------- */

  /* filter checkbox + sort select -> refine from the current form state */
  form.addEventListener('change', function (e) {
    if (e.target.closest('[data-hph-filter-checkbox]') || e.target.closest('[data-hph-sort-select]')) {
      run(buildFormUrl());
    }
  });

  /* price Apply / Enter -> form submit, intercepted. On mobile, Apply is a terminal action:
     close the drawer so the refined grid is revealed (otherwise the results update behind the
     full-screen drawer and nothing appears to happen). */
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    run(buildFormUrl());
    if (isDrawerOpen()) closeDrawer();
  });

  section.addEventListener('click', function (e) {
    /* let modified clicks (new tab, etc.) behave natively */
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    var link = e.target.closest(LINK_SELECTOR);
    if (link && link.getAttribute('href')) {
      e.preventDefault();
      run(link.getAttribute('href'));
      return;
    }

    var more = e.target.closest('[data-hph-load-more]');
    if (more) { e.preventDefault(); loadMore(more); return; }

    var gb = e.target.closest('[data-grid-cols]');
    if (gb && section.querySelector('[data-hph-grid-toggle]')) {
      localStorage.setItem('hph-grid-cols', gb.getAttribute('data-grid-cols'));
      applyGridDensity();
      return;
    }

    if (e.target.closest('[data-hph-sidebar-toggle]')) { openDrawer(); return; }
    if (e.target.closest('[data-hph-sidebar-close]')) { closeDrawer(); return; }
    if (e.target.closest('[data-hph-sidebar-backdrop]')) { closeDrawer(); return; }
    /* "Show N results" bar: commit any pending price input + reveal the grid. Checkboxes already
       refined live, but a freshly typed price only applies on this action, so run() then close. */
    if (e.target.closest('[data-hph-sidebar-apply]')) { run(buildFormUrl()); closeDrawer(); return; }

    var rm = e.target.closest('[data-hph-read-more]');
    if (rm) {
      var text = section.querySelector('[data-hph-desc-clamp] .hph-collection__description-text');
      if (text) {
        var expanded = text.classList.toggle('hph-collection__description-text--expanded');
        rm.textContent = expanded ? 'Read less' : 'Read more';
      }
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* back / forward */
  window.addEventListener('popstate', function () {
    if (!document.getElementById('hph-filter-form')) return;
    run(location.href, { push: false });
  });

  /* ---------- init ---------- */
  applyGridDensity();
  hideNumberedPagination();
})();
